import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type FC,
    type FocusEvent,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { formatRange, getInputRangeMinMax } from '../sbSharedRefineMenuHelpers.ts';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';
import { useAppliedFilterUrlLabel } from '../../helpers/appliedFilters/useAppliedFilterUrlLabel.ts';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { type TrackEventEventArgument } from '../../../utils/tracking/searchBrowse/filterTracking.ts';
import { getIsRangeFiltersUiVariant } from '../../../utils/abTestHelper.ts';

import { SbSharedRefineMenuInputRangeApplyButton } from './SbSharedRefineMenuInputRangeApplyButton.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import { SbSharedLabeledInput } from '../../SbSharedLabeledInput/SbSharedLabeledInput.jsx';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbSharedRefineMenuInputRange.module.css';

import { type SbSharedRefineMenuInputRange_itemSearch$key } from './__generated__/SbSharedRefineMenuInputRange_itemSearch.graphql.ts';
import { type SbSharedRefineMenuInputRange_filterValue$key } from './__generated__/SbSharedRefineMenuInputRange_filterValue.graphql.ts';

const messages = defineMessages({
    to: {
        id: 'sb.SbMobileRefineMenu.inputRange.to',
        defaultMessage: 'to',
    },
    error: {
        id: 'sb.SbMobileRefineMenu.inputRange.error',
        defaultMessage: 'Min value may not exceed max',
    },
    applyLabel: {
        id: 'sb.SbMobileRefineMenu.inputRange.applyLabel',
        defaultMessage: 'Apply range',
    },
    min: {
        id: 'sb.SbMobileRefineMenu.inputRange.min',
        defaultMessage: 'minimum',
    },
    max: {
        id: 'sb.SbMobileRefineMenu.inputRange.max',
        defaultMessage: 'maximum',
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuInputRange_itemSearch on ItemSearchQueryConnection {
        ...useAppliedFilterUrlLabel_itemSearch
    }
`;

const filterFragment = graphql`
    fragment SbSharedRefineMenuInputRange_filterValue on SearchBrowseFilterValue {
        linkReference
        stats {
            key
            values
        }
    }
`;

const parseMinValue = (min: number | string | null | undefined): string | null => {
    const minParsed = Math.floor(parseInt(String(min)));
    return typeof minParsed === 'number' && !isNaN(minParsed) ? minParsed.toString() : null;
};

const parseMaxValue = (max: number | string | null | undefined): string | null => {
    const maxParsed = Math.ceil(parseInt(String(max)));
    return typeof maxParsed === 'number' && !isNaN(maxParsed) ? maxParsed.toString() : null;
};

const isInvalidRange = (
    min: string | number | null | undefined,
    max: string | number | null | undefined
): boolean => {
    return parseInt(String(min)) > parseInt(String(max)) || parseInt(String(max)) <= 0;
};

const isDifferentValue = (
    currentValue: string,
    previousValue: string | number | null | undefined
): boolean => currentValue !== String(previousValue);

const clampRangeValues = (
    minInput: string | null,
    maxInput: string | null,
    availableMin: string | null | undefined,
    availableMax: string | null | undefined,
    disregardAvailableRange: boolean | null | undefined
): [string | null, string | null] => {
    let nextMin = minInput;
    let nextMax = maxInput;

    if (!disregardAvailableRange && availableMin && nextMin) {
        nextMin = parseInt(nextMin) < parseInt(availableMin) ? availableMin : nextMin;
    }
    if (!disregardAvailableRange && availableMax && nextMax) {
        nextMax = parseInt(nextMax) > parseInt(availableMax) ? availableMax : nextMax;
    }

    return [nextMin, nextMax];
};

type Props = {
    itemSearch: SbSharedRefineMenuInputRange_itemSearch$key;
    filterValue: SbSharedRefineMenuInputRange_filterValue$key | null | undefined;
    labelTo?: ReactNode;
    labelFrom?: ReactNode;
    filterName?: string | null;
    additionalFilter?: string;
    statKey?: string;
    applyOnBlur?: boolean;
    showInvalidRangeErrorMessage?: boolean;
    showAnimatedPlaceholder?: boolean;
    size?: 'small' | 'medium' | 'large';
    disregardAvailableRange?: boolean | null;
    trackingOptions?: Record<string, unknown>;
    ariaDescribedBy?: string;
    trackCustomRange?: boolean;
};

export const SbSharedRefineMenuInputRange: FC<Props> = ({
    itemSearch: itemSearchRef,
    filterValue: filterValueRef,
    applyOnBlur = false,
    labelFrom,
    labelTo,
    showInvalidRangeErrorMessage = true,
    size,
    ariaDescribedBy,
    disregardAvailableRange,
    trackCustomRange = false,
    showAnimatedPlaceholder,
    filterName,
    statKey,
    additionalFilter,
    trackingOptions,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filterValue = useFragment(filterFragment, filterValueRef);
    const intl = useIntl();
    const dispatch = useDispatch();
    const { currency } = useCurrency();
    const { isDesktop } = useServerVarsContext();
    const isClient = useClientState();
    const { refetchInFlight } = useSbSelector(state => state.filters);
    const isRangeFiltersUiVariant = isClient && getIsRangeFiltersUiVariant();

    const { linkReference, stats } = filterValue || {};
    const availableStat = stats?.find(stat => stat?.key === statKey);
    const statsValue = availableStat?.values;
    const urlLabel = useAppliedFilterUrlLabel(itemSearch, filterName);

    const { min, max, availableMin, availableMax } = getInputRangeMinMax({
        filterName,
        statsValue,
        urlLabel,
    });

    const [minValue, setMinValue] = useState<string | null>(() => parseMinValue(min));
    const [maxValue, setMaxValue] = useState<string | null>(() => parseMaxValue(max));
    const [error, setError] = useState(false);
    const [showAnimatedPlaceholderMin, setShowAnimatedPlaceholderMin] =
        useState(!!showAnimatedPlaceholder);
    const [showAnimatedPlaceholderMax, setShowAnimatedPlaceholderMax] =
        useState(!!showAnimatedPlaceholder);

    const prevMin = useRef(min);
    const prevMax = useRef(max);
    const hasChangedMinRef = useRef(false);
    const hasChangedMaxRef = useRef(false);
    const prevShowAnimatedPlaceholder = useRef(showAnimatedPlaceholder);

    const onApply = useCallback(
        (event: TrackEventEventArgument) => {
            const [nextMinValue, nextMaxValue] = clampRangeValues(
                minValue,
                maxValue,
                availableMin,
                availableMax,
                disregardAvailableRange
            );

            setMinValue(nextMinValue);
            setMaxValue(nextMaxValue);
            setShowAnimatedPlaceholderMin(false);
            setShowAnimatedPlaceholderMax(false);
            hasChangedMinRef.current = false;
            hasChangedMaxRef.current = false;

            let uriRef = formatRange(linkReference, [nextMinValue, nextMaxValue]);
            if (statKey && additionalFilter) {
                uriRef = uriRef.replace(`{${additionalFilter}}`, statKey.toLowerCase());
            }
            dispatch(
                updateUriRef({
                    filterName,
                    uriRef,
                    ga: trackCustomRange ? { label: `custom ${filterName} range` } : {},
                    trackingOptions,
                    event,
                })
            );
        },
        [
            additionalFilter,
            availableMax,
            availableMin,
            disregardAvailableRange,
            dispatch,
            filterName,
            linkReference,
            minValue,
            maxValue,
            statKey,
            trackCustomRange,
            trackingOptions,
        ]
    );

    useEffect(() => {
        const newMaxValue = parseMaxValue(max);
        const newMinValue = parseMinValue(min);

        if (prevShowAnimatedPlaceholder.current !== showAnimatedPlaceholder) {
            setShowAnimatedPlaceholderMin(!!showAnimatedPlaceholder);
            setShowAnimatedPlaceholderMax(!!showAnimatedPlaceholder);
            setMaxValue(newMaxValue);
            setMinValue(newMinValue);
            hasChangedMinRef.current = false;
            hasChangedMaxRef.current = false;
        }

        if (prevMax.current !== max) {
            setMaxValue(newMaxValue);
            hasChangedMaxRef.current = false;
        }

        if (prevMin.current !== min) {
            setMinValue(newMinValue);
            hasChangedMinRef.current = false;
        }

        // Checking both props and state validity, because both changes can invoke component update
        // depending on how range values were provided: by choosing predefined range or manually filling fields
        const hasError = isInvalidRange(min, max) || isInvalidRange(minValue, maxValue);
        setError(hasError);

        prevShowAnimatedPlaceholder.current = showAnimatedPlaceholder;
        prevMin.current = min;
        prevMax.current = max;
    }, [min, max, showAnimatedPlaceholder, minValue, maxValue]);

    const applyValue = useCallback(
        (event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLElement>) => {
            event.preventDefault();
            if (!error && (hasChangedMinRef.current || hasChangedMaxRef.current)) {
                onApply(event);
            }
        },
        [error, onApply]
    );

    const handleMinChange = useCallback(
        (value: string) => {
            hasChangedMinRef.current = isDifferentValue(value, min);
            setMinValue(value);
            setShowAnimatedPlaceholderMin(false);
        },
        [min]
    );

    const handleMaxChange = useCallback(
        (value: string) => {
            hasChangedMaxRef.current = isDifferentValue(value, max);
            setMaxValue(value);
            setShowAnimatedPlaceholderMax(false);
        },
        [max]
    );

    const onBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            event.preventDefault();
            if (!error && applyOnBlur && (hasChangedMinRef.current || hasChangedMaxRef.current)) {
                onApply(event);
            }
        },
        [applyOnBlur, error, onApply]
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (
                event.key === 'Enter' &&
                isRangeFiltersUiVariant &&
                !error &&
                !refetchInFlight &&
                (hasChangedMinRef.current || hasChangedMaxRef.current)
            ) {
                event.preventDefault();
                onApply(event);
            }
        },
        [error, isRangeFiltersUiVariant, onApply, refetchInFlight]
    );

    return (
        <>
            <div
                className={classnames(
                    dibsCss.flex,
                    isDesktop ? [dibsCss.pt1px, dibsCss.pbSmall] : dibsCss.pySmall
                )}
            >
                <SbSharedLabeledInput
                    ariaDescribedBy={ariaDescribedBy}
                    ariaLabel={labelFrom ? undefined : intl.formatMessage(messages.min)}
                    label={labelFrom}
                    size={size}
                    currency={currency}
                    value={minValue}
                    onBlur={onBlur}
                    onChange={handleMinChange}
                    dataTn="search-browse-currency-input-min"
                    name="price-min"
                    hasError={error}
                    onKeyDown={onKeyDown}
                    showAnimatedPlaceholder={showAnimatedPlaceholderMin}
                    disabled={refetchInFlight}
                />
                <div
                    className={classnames(
                        dibsCss.selfCenter,
                        isDesktop
                            ? [dibsCss.mxXsmall, dibsCss.sassyFontBodySizeSmallTypeNone]
                            : dibsCss.mxSmall
                    )}
                >
                    {intl.formatMessage(messages.to)}
                </div>
                <SbSharedLabeledInput
                    ariaDescribedBy={ariaDescribedBy}
                    ariaLabel={labelTo ? undefined : intl.formatMessage(messages.max)}
                    label={labelTo}
                    size={size}
                    currency={currency}
                    value={maxValue}
                    onBlur={onBlur}
                    onChange={handleMaxChange}
                    dataTn="search-browse-currency-input-max"
                    name="price-max"
                    hasError={error}
                    onKeyDown={onKeyDown}
                    showAnimatedPlaceholder={showAnimatedPlaceholderMax}
                    disabled={refetchInFlight}
                />
                {!applyOnBlur &&
                    isClient &&
                    (isRangeFiltersUiVariant ? (
                        <SbSharedRefineMenuInputRangeApplyButton
                            disabled={refetchInFlight}
                            hasError={error}
                            onClick={applyValue}
                        />
                    ) : (
                        <Button
                            ariaLabel={intl.formatMessage(messages.applyLabel)}
                            type="secondary"
                            size={size || 'large'}
                            onClick={applyValue}
                            className={classnames(styles.inputRangeButton, dibsCss.flexShrink0)}
                            isDangerousAction={error}
                            dataTn={`input-range-apply${error ? '-error' : ''}`}
                            disabled={refetchInFlight}
                        >
                            <ArrowRight className={classnames(dibsCss.w16px, dibsCss.hFull)} />
                        </Button>
                    ))}
            </div>
            {error && showInvalidRangeErrorMessage && (
                <div
                    className={classnames(
                        dibsCss.sassyFontBodySizeXSmallTypeNone,
                        dibsCss.textAlertprimary,
                        dibsCss.mbSmaller
                    )}
                >
                    {intl.formatMessage(messages.error)}
                </div>
            )}
        </>
    );
};
